import { useState } from 'react';
import { Building2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { KordaroLogo, KordaroMark } from '../../../core/shared/components/KordaroLogo.js';

interface RegistratieForm {
  winkelnaam: string;
  adres: string;
  stad: string;
  postcode: string;
  contactNaam: string;
  email: string;
  telefoon: string;
}

interface RegistratieResult {
  entityId: string;
  userId: string;
  email: string;
  tempPassword: string;
}

export function OnboardingRegistratiePage() {
  const [form, setForm] = useState<RegistratieForm>({
    winkelnaam: '',
    adres: '',
    stad: '',
    postcode: '',
    contactNaam: '',
    email: '',
    telefoon: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RegistratieResult | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.winkelnaam.trim() || !form.contactNaam.trim() || !form.email.trim()) {
      setError('Vul alle verplichte velden in');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/onboarding/registreer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message ?? 'Registratie mislukt');
        return;
      }
      setResult(data as RegistratieResult);
    } catch {
      setError('Verbindingsfout. Controleer uw internetverbinding.');
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-brand-secondary px-8 py-6">
              <KordaroLogo variant="ivory" width={160} />
            </div>
            <div className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="text-green-500" size={48} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Uw winkel is aangemaakt!</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Een welkomstmail is verstuurd naar <strong>{result.email}</strong> met uw inloggegevens.
              </p>
              <div className="bg-gray-50 border-l-4 border-brand rounded-lg p-4 text-left mb-6">
                <p className="text-xs text-gray-500 font-medium mb-1">E-mailadres</p>
                <p className="font-mono font-bold text-gray-800 text-sm">{result.email}</p>
                <p className="text-xs text-gray-500 font-medium mt-3 mb-1">Tijdelijk wachtwoord</p>
                <p className="font-mono font-bold text-gray-800 text-sm">{result.tempPassword}</p>
              </div>
              <p className="text-xs text-gray-500 mb-6">
                Bewaar deze gegevens goed. U kunt uw wachtwoord na het inloggen wijzigen.
              </p>
              <a
                href="/"
                className="inline-block w-full bg-brand text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#b8973d] transition-colors text-center"
              >
                Ga naar inlogpagina
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-brand-secondary px-8 py-6 flex items-center gap-3">
            <KordaroLogo variant="ivory" width={160} />
          </div>

          <div className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
                <Building2 className="text-brand" size={20} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Nieuwe winkel registreren</h1>
                <p className="text-sm text-gray-500">Stap 1 van 5 — Registratie</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-red-700 text-sm">
                <AlertCircle size={16} className="shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Winkelnaam <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="winkelnaam"
                  value={form.winkelnaam}
                  onChange={handleChange}
                  placeholder="bijv. IXINA Mechelen"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                  <input
                    type="text"
                    name="adres"
                    value={form.adres}
                    onChange={handleChange}
                    placeholder="Straat 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <input
                    type="text"
                    name="postcode"
                    value={form.postcode}
                    onChange={handleChange}
                    placeholder="2800"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stad</label>
                <input
                  type="text"
                  name="stad"
                  value={form.stad}
                  onChange={handleChange}
                  placeholder="Mechelen"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>

              <hr className="border-gray-100" />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naam contactpersoon <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contactNaam"
                  value={form.contactNaam}
                  onChange={handleChange}
                  placeholder="Voornaam Achternaam"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mailadres <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="admin@uwwinkel.be"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer</label>
                <input
                  type="tel"
                  name="telefoon"
                  value={form.telefoon}
                  onChange={handleChange}
                  placeholder="+32 ..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#b8973d] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Aanmaken...
                  </>
                ) : (
                  'Winkel aanmaken'
                )}
              </button>

              <p className="text-center text-sm text-gray-500">
                Al een account?{' '}
                <a href="/" className="text-brand font-medium hover:underline">
                  Inloggen
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
